import React, { useState, useEffect } from 'react'
import { FormData, FinancialsGrowthRequestBody } from '../Config/types'
import { defaultGrowthPeriods } from '../Config/defaults'
import {
  NAVBAR_HEIGHT_DESKTOP,
  DRAWER_WIDTH_DESKTOP,
  DRAWER_WIDTH_MOBILE_MAX,
  DRAWER_WIDTH_MOBILE_PERCENT,
  DRAWER_PADDING,
  CONTENT_PADDING,
  CONTENT_INNER_PADDING,
  ERROR_AUTO_DISMISS_TIME,
} from '../Config/layoutConstants'
import Spinner from '../Components/Spinner'
import OverviewContent from '../Components/OverviewContent'
import ValuationContent from '../Components/ValuationContent'
import GrowthContent from '../Components/GrowthContent'
import ValuationInputForm from '../Components/ValuationInputForm'
import NavBar from '../Components/NavBar'
import { Drawer, Box } from '@mui/material'
import { useValuationQuery, useGrowthQuery } from '../hooks/queries'
import { ApiError } from '../requests/api'
import './OutputView.css'

type TabType = 'overview' | 'valuation' | 'growth'

export const OutputView: React.FC = () => {
  // The submitted form data that triggers queries
  const [submittedFormData, setSubmittedFormData] = useState<FormData | null>(null)
  const [selectedTab, setSelectedTab] = useState<TabType>('overview')
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
  // Track dismissed error IDs to hide them until next query
  const [dismissedErrorIds, setDismissedErrorIds] = useState<Set<string>>(new Set())

  // Create the growth request body from the form data
  const growthRequestBody: FinancialsGrowthRequestBody | null = submittedFormData
    ? { symbols: submittedFormData.symbols, periods: defaultGrowthPeriods }
    : null

  // Query hooks - they automatically fetch when submittedFormData changes
  const valuationQuery = useValuationQuery(submittedFormData)
  const growthQuery = useGrowthQuery(growthRequestBody)

  // Derived loading state
  const isLoading = valuationQuery.isFetching || growthQuery.isFetching

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen)
  }

  const handleTabChange = (tab: TabType) => {
    setSelectedTab(tab)
  }

  const handleFormSubmit = (formData: FormData) => {
    // Clear dismissed errors when submitting new query
    setDismissedErrorIds(new Set())
    // Setting the submitted form data will trigger the queries
    setSubmittedFormData(formData)
  }

  const handleReset = () => {
    setSubmittedFormData(null)
    setDismissedErrorIds(new Set())
  }

  // Format errors for display
  const getErrors = (): Array<{ id: string; message: string; type: 'error' }> => {
    const errors: Array<{ id: string; message: string; type: 'error' }> = []
    
    if (valuationQuery.error) {
      const error = valuationQuery.error
      const message = error instanceof ApiError 
        ? error.message 
        : `Valuation error: ${error.message}`
      errors.push({ id: 'valuation-error', message, type: 'error' })
    }
    
    if (growthQuery.error) {
      const error = growthQuery.error
      const message = error instanceof ApiError 
        ? error.message 
        : `Growth data error: ${error.message}`
      errors.push({ id: 'growth-error', message, type: 'error' })
    }
    
    // Filter out dismissed errors
    return errors.filter(error => !dismissedErrorIds.has(error.id))
  }

  const handleDismissError = (errorId: string) => {
    setDismissedErrorIds(prev => new Set(prev).add(errorId))
  }

  // Auto-dismiss errors after timeout
  useEffect(() => {
    const errorIds: string[] = []
    if (valuationQuery.error && !dismissedErrorIds.has('valuation-error')) {
      errorIds.push('valuation-error')
    }
    if (growthQuery.error && !dismissedErrorIds.has('growth-error')) {
      errorIds.push('growth-error')
    }

    if (errorIds.length === 0) return

    const timeouts = errorIds.map(id =>
      setTimeout(() => {
        handleDismissError(id)
      }, ERROR_AUTO_DISMISS_TIME)
    )

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout))
    }
  }, [valuationQuery.error, growthQuery.error, dismissedErrorIds])

  const renderContent = () => {
    // Show spinner only on initial load when no data exists yet
    if (isLoading && !valuationQuery.data && !growthQuery.data) {
      return <Spinner />
    }

    switch (selectedTab) {
      case 'overview':
        return (
          <OverviewContent
            discountedCashFlowResponseData={valuationQuery.data ?? null}
            financialsGrowthResponseData={growthQuery.data ?? null}
            loading={isLoading}
            maximalGrowthRate={submittedFormData?.maximal_average_growth_rate}
          />
        )
      
      case 'valuation':
        return (
          <ValuationContent
            discountedCashFlowResponseData={valuationQuery.data ?? null}
            loading={valuationQuery.isFetching}
          />
        )
      
      case 'growth':
        return (
          <GrowthContent
            financialsGrowthResponseData={growthQuery.data ?? null}
            loading={growthQuery.isFetching}
          />
        )
      
      default:
        return null
    }
  }

  return (
    <>
      <NavBar
        selectedTab={selectedTab}
        onTabChange={handleTabChange}
        onDrawerToggle={handleDrawerToggle}
      />
      <Box sx={{ display: 'flex' }}>
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          variant="temporary"
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              top: `${NAVBAR_HEIGHT_DESKTOP}px`,
              height: `calc(100% - ${NAVBAR_HEIGHT_DESKTOP}px)`,
              width: DRAWER_WIDTH_MOBILE_PERCENT,
              maxWidth: DRAWER_WIDTH_MOBILE_MAX,
            },
          }}
        >
          <Box sx={{ p: DRAWER_PADDING }}>
            <ValuationInputForm
              onSubmit={handleFormSubmit}
              onReset={handleReset}
              isLoading={isLoading}
              errors={getErrors()}
              onDismissError={handleDismissError}
              onDrawerClose={handleDrawerToggle}
            />
          </Box>
        </Drawer>
        <Drawer
          anchor="left"
          open={true}
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              top: `${NAVBAR_HEIGHT_DESKTOP}px`,
              height: `calc(100% - ${NAVBAR_HEIGHT_DESKTOP}px)`,
              position: 'fixed',
              width: DRAWER_WIDTH_DESKTOP,
            },
          }}
        >
          <Box sx={{ p: DRAWER_PADDING }}>
            <ValuationInputForm
              onSubmit={handleFormSubmit}
              onReset={handleReset}
              isLoading={isLoading}
              errors={getErrors()}
              onDismissError={handleDismissError}
            />
          </Box>
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: CONTENT_PADDING,
            width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH_DESKTOP}px)` },
            ml: { xs: 0, md: `${DRAWER_WIDTH_DESKTOP}px` },
            boxSizing: 'border-box',
            maxWidth: '100vw',
          }}
        >
          <Box
            sx={{
              flex: 2,
              p: CONTENT_INNER_PADDING,
              overflowY: 'auto',
              bgcolor: 'background.paper',
              position: 'relative',
              maxHeight: { md: '85vh' },
            }}
          >
            {renderContent()}
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default OutputView
